import { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useClickAway } from 'react-use';
import Spinner, { SpinnerSize } from '../loading/spinner';

interface DropdownMenuReactPortalProps {
  isDropdownOpen: boolean;
  children: JSX.Element | JSX.Element[];
  buttonIconClasses: string;
  buttonClasses: string;
  buttonLabel?: string;
  buttonLabelClasses?: string;
  hasExtraButtonIconClasses?: boolean;
  extraButtonIconClasses?: string;
  dropdownMenuClasses?: string;
  containerClasses?: string;
  isDisabled?: boolean;
  title?: string;
  isLoading?: boolean;
  onDropdownClicked: () => void;
  onClickAway: () => void;
}

const DropdownMenuReactPortal = ({
  isDropdownOpen,
  children,
  buttonIconClasses,
  buttonClasses,
  buttonLabel,
  buttonLabelClasses,
  hasExtraButtonIconClasses,
  extraButtonIconClasses,
  dropdownMenuClasses,
  containerClasses,
  isDisabled = false,
  title,
  isLoading,
  onDropdownClicked,
  onClickAway
}: DropdownMenuReactPortalProps) => {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useClickAway(buttonRef, (event) => {
    if (
      dropdownRef.current &&
      event.target instanceof Node &&
      dropdownRef.current.contains(event.target)
    ) {
      return;
    }
    onClickAway();
  });

  const buttonLabelClassesToUse = buttonLabelClasses ?? 'ml-2 text-sm';

  const onClickPositionHandler = () => {
    const buttonRect = buttonRef.current?.getBoundingClientRect();
    if (buttonRect) {
      setPosition({
        top: buttonRect.top + buttonRect.height + window.scrollY,
        left: buttonRect.left + window.scrollX
      });
    }
    onDropdownClicked();
  };

  const dropdownContent = isDropdownOpen ? (
    <div
      ref={dropdownRef}
      className={`absolute drop-shadow-xl rounded ${
        dropdownMenuClasses ?? 'right-0 z-40'
      }`}
      style={{
        top: position?.top,
        left: position?.left
      }}
      role="menu"
    >
      <div className="bg-white flex flex-col border rounded border-neutral-200">
        {children}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className={containerClasses ?? 'block relative'} ref={buttonRef}>
        <button
          aria-haspopup="true"
          aria-controls="dropdown-menu"
          onClick={onClickPositionHandler}
          className={`${buttonClasses}`}
          disabled={isDisabled}
          type="button"
          title={title}
        >
          {isLoading ? (
            <Spinner size={SpinnerSize.SMALL} />
          ) : (
            <>
              <i className={buttonIconClasses}></i>
              {hasExtraButtonIconClasses && (
                <i className={extraButtonIconClasses}></i>
              )}
            </>
          )}
          {buttonLabel && (
            <span className={buttonLabelClassesToUse}>{buttonLabel}</span>
          )}
        </button>
        {isDropdownOpen &&
          ReactDOM.createPortal(dropdownContent, document.body)}
      </div>
    </>
  );
};

export default DropdownMenuReactPortal;
